import { translateBackend } from '@assets/i18n/config';
import { getLanguageDefaults } from '@onevesthq/ov-translations';
import { useTextFieldTokens } from '@ovComponents/2-component/textField/textField.tokens';
import { TranslatableTextField } from '@ovComponents/2-component/translatableTextField/translatableTextField';
import { Box, Typography, useTheme } from 'hephaestus/web';
import type { ReactNode } from 'react';
import { toEditableAllocations } from '../addModelPortfoliosIPSAndAssetClasses/assetClassTargetAllocation.utils';
import { AssetClassesTargetAllocations } from '../addModelPortfoliosIPSAndAssetClasses/components/assetClassesTargetAllocations';
import {
  DEFAULT_MODEL_PORTFOLIO_GROUP_BY,
  type ModelPortfolioGroupBy,
} from '../createModelPortfolio/modelPortfolioGroupBy';
import type {
  HouseholdIpsActuals,
  HouseholdIpsFieldDefinition,
  HouseholdIpsOptions,
  HouseholdIpsValues,
} from './householdIpsFields';

/** A label sitting above something that isn't a text field. */
export const IpsFieldLabel = ({
  children,
  testId,
}: {
  children: string;
  testId: string;
}) => {
  const theme = useTheme();
  const tokens = useTextFieldTokens(theme);

  return (
    <Typography
      data-testid={testId}
      variant='labelSmall'
      component='div'
      sx={{ mb: '4px', color: tokens.labelColor }}
    >
      {children}
    </Typography>
  );
};

export type ReadOnlyIpsFieldProps = {
  definition: HouseholdIpsFieldDefinition;
  options: HouseholdIpsOptions;
  values: HouseholdIpsValues;
  testIdPrefix: string;
};

/**
 * One IPS field of a model nobody is editing, drawn as the field it would be
 * edited in so it can be read beside the model being proposed.
 */
export const ReadOnlyIpsField = ({
  definition: { optionKey, valueKey, testId, richText },
  options,
  values,
  testIdPrefix,
}: ReadOnlyIpsFieldProps): ReactNode => {
  const option = options?.[optionKey];

  if (!option?.enabled) return null;

  if (richText) {
    return (
      <>
        <IpsFieldLabel testId={`${testIdPrefix}-${testId}-label`}>
          {translateBackend(option?.label)}
        </IpsFieldLabel>
        <Box
          data-testid={`${testIdPrefix}-${testId}`}
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            p: 2,
            minHeight: '96px',
            // Fills the row so it ends level with the editor it is being read
            // against, which is taller by the height of its toolbar.
            flex: 1,
            color: 'text.disabled',
            '& p': { margin: 0 },
            '& p + p': { marginTop: 1 },
          }}
          // The disclaimer is written as rich text, so it is shown as the
          // markup it was written in rather than as escaped tags.
          dangerouslySetInnerHTML={{
            __html: translateBackend(values[valueKey] ?? undefined),
          }}
        />
      </>
    );
  }

  return (
    <TranslatableTextField
      testId={`${testIdPrefix}-${testId}`}
      label={translateBackend(option?.label)}
      // A field the model has nothing to say about still has to be drawn, or
      // the side being compared against would be missing a row.
      value={values[valueKey] ?? getLanguageDefaults()}
      onChange={() => undefined}
      disabled
      fullWidth
      multiline
      rows={1}
    />
  );
};

export type ReadOnlyIpsBandsProps = {
  options: HouseholdIpsOptions;
  values: HouseholdIpsValues;
  actuals?: HouseholdIpsActuals;
  groupBy?: ModelPortfolioGroupBy;
  compact?: boolean;
  testIdPrefix: string;
};

/**
 * The target allocation bands of a model nobody is editing. The actual weights
 * stay, since how they sit against each band is the reason the bands are worth
 * reading at all.
 */
export const ReadOnlyIpsBands = ({
  options,
  values,
  actuals,
  groupBy = DEFAULT_MODEL_PORTFOLIO_GROUP_BY,
  compact = false,
  testIdPrefix,
}: ReadOnlyIpsBandsProps): ReactNode => {
  if (!options?.enableAssetClassTargetAllocations) return null;

  return (
    <>
      <IpsFieldLabel
        testId={`${testIdPrefix}-household-ips-target-allocations-label`}
      >
        {translateBackend(options?.assetClassTargetAllocationsLabel)}
      </IpsFieldLabel>
      <AssetClassesTargetAllocations
        testId={`${testIdPrefix}-household-ips-target-allocations`}
        targetAllocations={toEditableAllocations(values.targetAllocations)}
        setTargetAllocations={() => undefined}
        securities={actuals?.securities}
        assetClassBranches={actuals?.assetClassBranches}
        strategy={actuals?.strategy}
        groupBy={groupBy}
        compact={compact}
        readOnly
      />
    </>
  );
};
